import { PlusOutlined } from '@ant-design/icons';
import { Drawer, DrawerProps, Button, message } from 'antd';
import { AxiosError } from 'axios';
import { Helmet } from 'react-helmet-async';

import useAPIMutation from '../api/useAPIMutation';
import { EmailCommunicationTemplateCollection } from '../types';
import EmailTemplateForm, { EmailTemplateFormValues } from './EmailTemplateForm';
import Form from './Form';
import { fixUnorderedListInQuill } from './RichEditor/RichEditor';

interface AddEmailTemplateProps extends DrawerProps {
  companyFromName?: string;
  enableAlchemerIntegration?: boolean;
  onSuccess?: (
    data: unknown,
    variables: EmailTemplateFormValues,
    context: unknown
  ) => void | Promise<unknown>;
  onError?: (
    error: AxiosError,
    variables: EmailTemplateFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const AddEmailTemplate = ({
  companyFromName,
  enableAlchemerIntegration,
  onSuccess,
  onError,
  ...props
}: AddEmailTemplateProps) => {
  const [form] = Form.useForm();
  const typeValue = Form.useWatch<EmailCommunicationTemplateCollection['type']>('type', form);

  const mutation = useAPIMutation(
    async (
      {
        name,
        subject,
        message,
        type,
        fromName,
        attachments,
        activeWelcomeTemplate,
      }: EmailTemplateFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments) {
        const attachmentFormData = new FormData();
        attachmentFormData.append('filename', attachment.name);
        attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
        attachmentFormData.append('mimetype', attachment.type as string);
        const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        attachmentIds.push(attachmentResponse.data.id);
      }

      const emailCommunicationTemplate = await api.post('/email-communication-templates/', {
        name,
        subject,
        from_name: fromName,
        type: type as EmailCommunicationTemplateCollection['type'],
        message: message.replace('\n', ''),
        individual: false,
        attachments: attachmentIds,
      });

      if (activeWelcomeTemplate) {
        api.patch('/configs/tenant-configurations/company-welcome-message-template-id/', {
          value: emailCommunicationTemplate.data.id,
        });
      }
    },
    {
      onError: (error: AxiosError) => {
        if (error?.response?.status === 413) {
          message.error(
            'O arquivo selecionado excede o limite de 2 MB. Por favor, envie um arquivo menor.'
          );
        } else {
          message.error('Erro ao salvar template.');
        }
      },
      onSuccess,
    }
  );

  return (
    <Drawer
      title="Adicionar modelo de e-mail"
      width={720}
      footer={
        <Button
          type="primary"
          htmlType="submit"
          icon={<PlusOutlined />}
          loading={mutation.isLoading}
          onClick={() => form.submit()}
        >
          Adicionar
        </Button>
      }
      {...props}
    >
      <Helmet>
        <title>Adicionar modelo de e-mail | Recrutamento e Seleção</title>
      </Helmet>
      <Form<EmailTemplateFormValues>
        form={form}
        layout="vertical"
        onFinish={(variables) => {
          mutation.mutate({ ...variables, message: fixUnorderedListInQuill(variables.message) });
        }}
        initialValues={{
          name: '',
          fromName: companyFromName,
          subject: '',
          message: '',
          type: '',
          attachments: [],
        }}
      >
        <EmailTemplateForm
          templateTypes={[
            { value: 'COMMUNICATION', label: 'Comunicação' },
            { value: 'TESTS', label: 'Testes Mindsight' },
            { value: 'ADDITIONAL_QUESTIONS', label: 'Perguntas de pré-requisito' },
            ...(enableAlchemerIntegration
              ? [{ value: 'CUSTOM_TESTS', label: 'Testes Customizados' }]
              : []),
            { value: 'WELCOME_MESSAGE', label: 'Mensagem boas-vindas' },
          ]}
          customVariables={[
            ...(typeValue === 'TESTS'
              ? [{ value: '[link_testes]', label: 'Link dos testes' }]
              : []),
            ...(typeValue === 'ADDITIONAL_QUESTIONS'
              ? [
                  {
                    value: '[link_perguntas_adicionais]',
                    label: 'Link Perguntas de pré-requisito',
                  },
                ]
              : []),
            ...(typeValue === 'CUSTOM_TESTS' && enableAlchemerIntegration
              ? [
                  {
                    value: '[link_testes_customizados]',
                    label: 'Link Testes Customizados',
                  },
                ]
              : []),
            { value: '[candidato]', label: 'Nome do Candidato' },
            { value: '[empresa]', label: 'Nome da Empresa' },
            { value: '[vaga]', label: 'Nome da Vaga' },
          ]}
          richEditorRules={[
            {
              validator(_, value: string) {
                const cleanHtmlRegex = /<[^>]*>/g;

                if (!value.replaceAll(cleanHtmlRegex, '').trim()) {
                  return Promise.reject(new Error('Campo obrigatório.'));
                }

                return Promise.resolve();
              },
            },
            typeValue === 'TESTS'
              ? {
                  validator: (_, value: string) => {
                    if (
                      value &&
                      (value.includes('[link_testes]') || value.includes('[LINK_TESTS]'))
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        'Para criar este modelo é necessário incluir a variável Link dos Testes'
                      )
                    );
                  },
                }
              : {},
            typeValue === 'CUSTOM_TESTS' && enableAlchemerIntegration
              ? {
                  validator(_, value) {
                    const variablesRegex = /\[link_testes_customizados\]/;
                    if (!variablesRegex.test(value)) {
                      return Promise.reject(
                        new Error(
                          'Para criar este modelo é necessário incluir a variável Link Testes Customizados'
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }
              : {},
            typeValue === 'ADDITIONAL_QUESTIONS'
              ? {
                  validator(_, value) {
                    const variablesRegex = /\[link_perguntas_adicionais\]/;
                    if (!variablesRegex.test(value)) {
                      return Promise.reject(
                        new Error(
                          'Para criar este modelo é necessário incluir a variável Link Perguntas de pré-requisito'
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }
              : {},
          ]}
          fromName
          attachments
        />
      </Form>
    </Drawer>
  );
};

export default AddEmailTemplate;
